/* eslint-disable @angular-eslint/no-empty-lifecycle-method */
import { Component, OnInit, ViewChild, TemplateRef, inject, AfterViewInit, viewChild, computed, Signal } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { CommonModule } from '@angular/common';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  createAngularTable,
  getCoreRowModel,
  ColumnDef,
  FlexRenderDirective,
  HeaderContext,
  CellContext
} from '@tanstack/angular-table';
import { IDssReport } from 'src/app/interfaces/dssReport.Interface';
import { addFooter, addHeader, addParagraph, addSubHeader, checkSectionOffPage, jsPDFCustom } from 'src/app/interfaces/JsPDF.extended.interface';
import { PaddockDexie } from 'src/app/models/paddock.model';
import { PropertyDexie } from 'src/app/models/property.model';
import { mapService } from 'src/app/services/map.service';
import { DssMapOverlayComponent } from './dss-map-overlay/dss-map-overlay.component';
import { DssReportSections, getDssOverViewSection } from './dss-report-pdf-data';
import { RoundingPipe } from '../../../../pipes/rounding.pipe';
import { OrdinalPipe } from '../../../../pipes/ordinal.pipe';

interface DssReportWithTable extends IDssReport {
  table?: any;
}

@Component({
  selector: 'app-dss-report',
  templateUrl: './dss-report.component.html',
  styleUrls: ['./dss-report.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatTabsModule,
    RoundingPipe,
    OrdinalPipe,
    FlexRenderDirective
  ]
})
export class DssReportComponent implements AfterViewInit {
  dialogRef = inject<MatDialogRef<DssReportComponent>>(MatDialogRef);
  private rawReports = inject<Array<IDssReport>>(MAT_DIALOG_DATA);
  private mapService = inject(mapService);
  private dialog = inject(MatDialog);



  rankHeader =
    viewChild.required<TemplateRef<{ $implicit: HeaderContext<any, any> }>>(
      'rankHeader'
    );

  rankCell =
    viewChild.required<TemplateRef<{ $implicit: CellContext<any, any> }>>(
      'rankCell'
    );

  imageHeader =
    viewChild.required<TemplateRef<{ $implicit: HeaderContext<any, any> }>>(
      'imageHeader'
    );

  imageCell =
    viewChild.required<TemplateRef<{ $implicit: CellContext<any, any> }>>(
      'imageCell'
    );

  cropHeader =
    viewChild.required<TemplateRef<{ $implicit: HeaderContext<any, any> }>>(
      'cropHeader'
    );

  cropCell =
    viewChild.required<TemplateRef<{ $implicit: CellContext<any, any> }>>(
      'cropCell'
    );

  wellSuitedHeader =
    viewChild.required<TemplateRef<{ $implicit: HeaderContext<any, any> }>>(
      'wellSuitedHeader'
    );

  wellSuitedCell =
    viewChild.required<TemplateRef<{ $implicit: CellContext<any, any> }>>(
      'wellSuitedCell'
    );

  suitableHeader =
    viewChild.required<TemplateRef<{ $implicit: HeaderContext<any, any> }>>(
      'suitableHeader'
    );

  suitableCell =
    viewChild.required<TemplateRef<{ $implicit: CellContext<any, any> }>>(
      'suitableCell'
    );

  moderatelySuitableHeader =
    viewChild.required<TemplateRef<{ $implicit: HeaderContext<any, any> }>>(
      'moderatelySuitableHeader'
    );

  moderatelySuitableCell =
    viewChild.required<TemplateRef<{ $implicit: CellContext<any, any> }>>(
      'moderatelySuitableCell'
    );

  unsuitableHeader =
    viewChild.required<TemplateRef<{ $implicit: HeaderContext<any, any> }>>(
      'unsuitableHeader'
    );

  unsuitableCell =
    viewChild.required<TemplateRef<{ $implicit: CellContext<any, any> }>>(
      'unsuitableCell'
    );

  actionsHeader =
    viewChild.required<TemplateRef<{ $implicit: HeaderContext<any, any> }>>(
      'actionsHeader'
    );

  actionsCell =
    viewChild.required<TemplateRef<{ $implicit: CellContext<any, any> }>>(
      'actionsCell'
    );

  columns =
    computed<ColumnDef<any>[]>(() => [
      {
        accessorKey: 'rank',
        header: () => this.rankHeader(),
        cell: () => this.rankCell(),
      },
      {
        accessorKey: 'image',
        header: () => this.imageHeader(),
        cell: () => this.imageCell(),
      },
      {
        accessorKey: 'crop',
        header: () => this.cropHeader(),
        cell: () => this.cropCell(),
      },
      {
        accessorKey: 'wellSuited',
        header: () => this.wellSuitedHeader(),
        cell: () => this.wellSuitedCell(),
      },
      {
        accessorKey: 'suitable',
        header: () => this.suitableHeader(),
        cell: () => this.suitableCell(),
      },
      {
        accessorKey: 'moderatelySuited',
        header: () => this.moderatelySuitableHeader(),
        cell: () => this.moderatelySuitableCell(),
      },
      {
        accessorKey: 'unsuitable',
        header: () => this.unsuitableHeader(),
        cell: () => this.unsuitableCell(),
      },
      {
        id: 'actions',
        header: () => this.actionsHeader(),
        cell: () => this.actionsCell(),
      },
    ]);

  dssReports: Signal<DssReportWithTable[]> = computed(() => {

    const columns = this.columns();

    return this.rawReports.map(report => ({
      ...report,
      table: createAngularTable(() => ({
        data: report.enterpriseReport.data,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
      }))
    }));

  });

  ngAfterViewInit(): void {

  }
  fixUrl(url: string): string {
    return url.replace(/https=\/\//g, 'https://');
  }

  async showCrop(crop: any, target: any) {
    await this.mapService.showCrop(crop.name, target);

    let enterpriseLayer = target instanceof PropertyDexie
      ? this.mapService.enterpriseSuitibilityGroupProperty
      : this.mapService.enterpriseSuitibilityGroupPaddock;

    if (target instanceof PaddockDexie) {
      await this.mapService.loadSelectedPaddocks([target]);
    }

    enterpriseLayer.setVisible(true);

    const dialogRef = this.dialog.open(DssMapOverlayComponent, {
      hasBackdrop: true,
      data: {
        map: this.mapService.map,
        layers: [...this.mapService.propertyLayerGroup.getLayersArray(), enterpriseLayer]
      },
      width: '40%'
    });

    dialogRef.afterClosed().subscribe(() => {
      this.mapService.loadSelectedPaddocks([]);
      enterpriseLayer.setVisible(false);
    });
  }

  getCellContext(cell: any, report: DssReportWithTable) {
  return {
    ...cell.getContext(),
    report,
  };
}

  export(report: IDssReport) {
    let target = report.target;
    let data = report.enterpriseReport.data;

    const pdf = new jsPDF() as jsPDFCustom;
    const margin = 10;
    const headerHeight = 30;
    const pageWidth = pdf.internal.pageSize.width;
    const textWidth = pageWidth - 2 * margin;

    let title = 'Crop Decision Report';

    addHeader(title, pdf, margin, headerHeight);
    addFooter(pdf, margin);

    const paragraphNewPageCallback = () => {
      pdf.addPage();
      addHeader(title, pdf, margin, headerHeight);
      addFooter(pdf, margin);
      return headerHeight + margin;
    };

    const contentTopMargin = 50;
    const contentBottomMargin = pdf.internal.pageSize.height - 30;

    let y = headerHeight + margin;

    let overviewSection = getDssOverViewSection(report.target, report.totalAreaNotCovered);

    y += addSubHeader(overviewSection.title, pdf, margin, y);
    y += margin;

    overviewSection.paragraphs.forEach(paragraph => {
      y = addParagraph(pdf, paragraph, margin, y, textWidth, margin, paragraphNewPageCallback);
      y += 5; // Space between paragraphs
    });

    y += margin;

    let headerData = addSubHeader("Data", pdf, margin, y);

    let destructuredData = data.map((obj) => {
      return [
        this.getNumberWithOrdinal(obj.rank),
        obj.crop.label,
        obj.wellSuited.toFixed(1),
        obj.suitable.toFixed(1),
        obj.moderatelySuitable.toFixed(1),
        obj.unsuitable.toFixed(1),
      ];
    });

    pdf.autoTable({
      head: [['Rank', 'Crop', 'Well Suited (ha)', 'Suitable (ha)', 'Moderately Suitable (ha)', 'Unsuitable (ha)']],
      body: destructuredData,
      startY: y + headerData + margin,
      didDrawPage: (data) => {
        addHeader(title, pdf, margin, headerHeight);
        addFooter(pdf, margin);
      },
      margin: { top: contentTopMargin }
    });

    pdf.addPage();
    addHeader(title, pdf, margin, headerHeight);
    addFooter(pdf, margin);
    y = headerHeight + margin;

    DssReportSections.forEach(section => {
      let offPage = checkSectionOffPage(pdf, margin, section, y);

      if (offPage) {
        pdf.addPage();
        addHeader(title, pdf, margin, headerHeight);
        addFooter(pdf, margin);
        y = headerHeight + margin;
      }

      y += addSubHeader(section.title, pdf, margin, y);
      y += margin;

      section.paragraphs.forEach(paragraph => {
        y = addParagraph(pdf, paragraph, margin, y, textWidth, margin, paragraphNewPageCallback);
        y += 5; // Space between paragraphs
      });
      y += margin;
    });

    pdf.save(`dss-report-${target.name.replace(" ", '-')}-${new Date().toUTCString()}.pdf`);
  }

  private getNumberWithOrdinal(n: number): string {
    var s = ["th", "st", "nd", "rd"],
      v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }
}
